import { IconContext } from "react-icons";
import { MdKeyboardArrowDown, MdKeyboardArrowLeft, MdKeyboardArrowUp, MdInfo } from "react-icons/md";
import styles from './detail.module.scss';
import { useNavigate, useParams } from "react-router-dom";
import { Deficode, Hoofdcode } from "@/models/hoofdcode";
import { useEffect, useState } from "react";
import apiService from "@/api-service";
import { Button, ButtonGroup, Col, Container, Form, Modal, Row, Spinner } from "react-bootstrap";
import HeaderedList from "@/components/headered-list/headered-list";
import HeaderedContainer from "@/components/headered-container/headered-container";
import HoofdcodeUsageChart from "@/components/charts/hoofdcode-usage/hoofdcode-usage";
import BestekFilter from "@/components/bestek-filter/bestek-filter";
import globalStyles from "@/styles/app.module.scss";
import { BestekFilterParams, initialBestekFilterParams, mapFilterParamsToQueryParams } from "@/components/bestek-filter/bestek-filter-params";
import Select from "react-select";
import useLoading from "@/hooks/loading.hook";
import useAPIDownload from "@/hooks/download.hook";
import { Bestek } from "@/models/bestek";
import BestekkenList from "@/components/bestek-list/bestek-list";

export default function HoofdcodeDetail() {
    const navigate = useNavigate();
    const { id } = useParams();
    const loader = useLoading();
    const downloadFromAPI = useAPIDownload();

    const [ hc, setHC ] = useState<Hoofdcode>();
    const [ usageChartFilterParamsExpanded, setUsageChartFilterParamsExpanded ] = useState<boolean>(false);
    const [ initialUsageChartFilterParams, setInitialUsageChartFilterParams ] = useState<BestekFilterParams>();
    const [ usageChartFilterParams, setUsageChartFilterParams ] = useState<BestekFilterParams>(initialBestekFilterParams);

    const [ bestekListFilterParamsQuery, setBestekListFilterParamsQuery ] = useState<any>({});
    const [ bestekListBestekken, setBestekListBestekken ] = useState<Bestek[]>();
    const [ bestekListCount, setBestekListCount ] = useState<number>(-1);
    const [ bestekListPage, setBestekListPage ] = useState<number>(1);
    const [ bestekListLoading, setBestekListLoading ] = useState<boolean>(true);

    const [ deficodeMode, setDeficodeMode ] = useState<boolean>(false);
    const [ selectedDeficode, setSelectedDeficode ] = useState<Deficode>();

    const [ isExporting, setIsExporting ] = useState<boolean>(false);
    
    const [ isInitialLoading, setIsInitialLoading ] = useState<boolean>(true);
    const [ initialLoadComplete, setInitialLoadComplete ] = useState<boolean>(false);
    const [ preloadedChartData, setPreloadedChartData ] = useState<any>(null);
    
    const is99Code = parseInt(id ?? "0") < 0 || hc?.volgnummer === '99';

    useEffect(() => {
        const currentId = parseInt(id ?? "0");
        
        if (!hc || hc.id !== currentId) {
            console.log('🚀 Loading hoofdcode detail via /complete endpoint...');
            setIsInitialLoading(true);
            setBestekListLoading(true);
            setInitialLoadComplete(false);
            
            if (currentId < 0) {
                apiService.get(`hoofdcode/${id}`)
                    .then((response) => {
                        setHC(response.data);

                        if (!initialUsageChartFilterParams) {
                            const initialParams = Object.assign({ ...initialBestekFilterParams }, { hoofdcodes: [response.data] });
                            setInitialUsageChartFilterParams(initialParams);
                            handleUsageChartFilterParamsChange({ ...initialParams });
                        }
                        
                        setInitialLoadComplete(true);
                    })
                    .catch(console.error)
                    .finally(() => setIsInitialLoading(false));
            } else {
                apiService.get(`hoofdcode/${id}/complete`)
                    .then((response) => {
                        console.log('✅ Complete data loaded:', response.data);
                        
                        setHC(response.data.hoofdcode);
                        
                        setBestekListBestekken(response.data.bestekken.result);
                        setBestekListCount(response.data.bestekken.total_count);
                        setBestekListLoading(false);
                        
                        if (response.data.chart_data) {
                            setPreloadedChartData(response.data.chart_data);
                            console.log('✅ Chart data preloaded');
                        }
                        
                        if (!initialUsageChartFilterParams) {
                            const initialParams = Object.assign(
                                { ...initialBestekFilterParams }, 
                                { hoofdcodes: [response.data.hoofdcode] }
                            );
                            setInitialUsageChartFilterParams(initialParams);
                            handleUsageChartFilterParamsChange({ ...initialParams });
                        }
                        
                        setInitialLoadComplete(true);
                        
                        console.log('✅ Initial load complete!');
                    })
                    .catch((error) => {
                        console.error('❌ Complete endpoint failed:', error);
                        setBestekListLoading(false);
                        setInitialLoadComplete(true);
                    })
                    .finally(() => setIsInitialLoading(false));
            }
        }
    }, [id]);

    useEffect(() => {
        if (!initialLoadComplete) {
            return;
        }
        
        const isDefaultFilter = 
            Object.keys(bestekListFilterParamsQuery).length === 0 ||
            Object.keys(bestekListFilterParamsQuery).every(key => 
                key === 'hoofdcode_ids' || key === 'zoek_categorien'
            );
        
        // FIXED: 99-codes (negatief ID) laden nooit via /complete — dat endpoint bestaat
        // alleen voor reguliere hoofdcodes (zie de `currentId < 0`-tak hierboven, die alleen
        // `hoofdcode/${id}` aanroept en géén bestekken-data zet). Zonder deze uitzondering
        // werd deze refresh voor 99-codes altijd overgeslagen ("gebruik data van /complete"
        // terwijl die er nooit was), en bleef de bestekken-tabel oneindig op "laden" staan.
        if (!is99Code && bestekListPage === 1 && isDefaultFilter && !deficodeMode) {
            console.log('⏭️ Skipping bestek refresh - using data from /complete');
            return;
        }
        
        console.log('🔄 Refreshing bestek list (filter/page change)...');
        setBestekListLoading(true);
        
        if (is99Code && hc?.werk_categorie_code && hc?.subwerk_categorie_code) {
            const params = {
                werk_categorie: hc.werk_categorie_code,
                subwerk_categorie: hc.subwerk_categorie_code,
                // Elke jaarversie van een 99-code is een eigen entiteit (net als reguliere
                // codes) — zonder dit zou de bestekken-tabel alle jaarversies door elkaar
                // tonen, terwijl de tegel erboven maar 1 specifieke jaarversie toont.
                versie_jaarversie: hc.versie_jaarversie,
                page: bestekListPage,
                ...bestekListFilterParamsQuery
            };
            
            apiService.post(`/hoofdcode/99-codes/bestekken`, params)
                .then((response) => {
                    setBestekListBestekken(response.data.result || []);
                    setBestekListCount(response.data.total_count || 0);
                })
                .catch((error) => {
                    console.error('99 code bestekken search failed:', error);
                    setBestekListBestekken([]);
                    setBestekListCount(0);
                })
                .finally(() => setBestekListLoading(false));
                
        } else if (!is99Code) {
            apiService.post<{ result: Bestek[], total_count: number }>('/bestek/search', {
                search_params: bestekListFilterParamsQuery,
                include_hoofdcode_usage_for: hc?.id,
                include_deficode_usage_for: deficodeMode ? selectedDeficode?.id : null,
                page: bestekListPage
            }).then((response) => {
                setBestekListBestekken(response.data.result);
                setBestekListCount(response.data.total_count);
            }, (error) => {
                console.error('Regular bestek search failed:', error);
                setBestekListBestekken([]);
                setBestekListCount(0);
            }).finally(() => setBestekListLoading(false));
        } else {
            setBestekListLoading(false);
            setBestekListBestekken([]);
            setBestekListCount(0);
        }
    }, [bestekListFilterParamsQuery, bestekListPage, deficodeMode, selectedDeficode, initialLoadComplete]);

    const handleUsageChartFilterParamsChange = (params?: BestekFilterParams) => {
        if (params) {
            setBestekListFilterParamsQuery(mapFilterParamsToQueryParams(params));
            setUsageChartFilterParams(params);
        }
    }

    const handleDeficodeModeChange = (enabled: boolean) => {
        setDeficodeMode(enabled);
        if (!enabled) {
            handleUsageChartFilterParamsChange(Object.assign({ ...usageChartFilterParams }, { deficodes: [] }));
        } else {
            const firstDeficode = (hc?.deficodes?.length ?? 0) > 0 ? hc?.deficodes?.[0] : undefined;
            if (!selectedDeficode) {
                handleSelectedDeficodeChange(firstDeficode);
            }
            const activeDeficode = selectedDeficode ?? firstDeficode;
            handleUsageChartFilterParamsChange(Object.assign({ ...usageChartFilterParams }, { deficodes: activeDeficode ? [activeDeficode] : [] }));
        }
    }

    const handleSelectedDeficodeChange = (deficode?: Deficode) => {
        setSelectedDeficode(deficode);

        if (deficode) {
            handleUsageChartFilterParamsChange(Object.assign({ ...usageChartFilterParams}, { deficodes: [deficode] }));
        } else {
            handleUsageChartFilterParamsChange(Object.assign({ ...usageChartFilterParams}, { deficodes: [] }));
        }
    }

    const download = (format: 'csv' | 'xlsx'): void => {
        if (isExporting) {
            return;
        }

        setIsExporting(true);
        loader.show("Export voorbereiden...");

        let exportData;
        
        if (is99Code) {
            exportData = {
                bestek_filter: {
                    ...mapFilterParamsToQueryParams(usageChartFilterParams),
                    hoofdcode_werk_categorie_ids: [hc?.werk_categorie?.id].filter(Boolean),
                    hoofdcode_subwerk_categorie_ids: [hc?.subwerk_categorie?.id].filter(Boolean),
                    volgnr_99: true
                }, 
                format: format, 
                hoofdcode_id: null,
                deficode_id: null,
                is_99_code_export: true,
                werk_categorie_code: hc?.werk_categorie_code,
                subwerk_categorie_code: hc?.subwerk_categorie_code,
                // Zonder dit exporteert de backend alle jaarversies van deze 99-code
                // categorie door elkaar, terwijl de pagina er maar 1 specifieke toont.
                versie_jaarversie: hc?.versie_jaarversie
            };
        } else {
            exportData = {
                bestek_filter: usageChartFilterParams ? mapFilterParamsToQueryParams(usageChartFilterParams) : {}, 
                format: format, 
                hoofdcode_id: id, 
                deficode_id: deficodeMode ? selectedDeficode?.id : null
            };
        }

        const filename = is99Code 
            ? `gebruik_99_codes_${hc?.werk_categorie_code}_${hc?.subwerk_categorie_code}.${format}`
            : `gebruik_hoofdcode_${hc?.label}` + 
              (deficodeMode ? `_deficode_${selectedDeficode?.positie}_${selectedDeficode?.inhoud_nr}` : '') + 
              `.${format}`;

        downloadFromAPI(
            { 
                method: 'post', 
                path: 'hoofdcode/detail-export',
                data: exportData
            },
            filename,
            {
                finally: () => {
                    loader.hide();
                    setIsExporting(false);
                }
            }
        );
    }

    const werkcat_items = [
        { label: "Code:", value: hc?.werk_categorie_code ?? "" },
        { label: "Omschrijving:", value: hc?.werk_categorie?.omschrijving ?? "" }
    ];

    const subcat_items = [
        { label: "Code:", value: hc?.subwerk_categorie_code ?? "" },
        { label: "Omschrijving:", value: hc?.subwerk_categorie?.omschrijving ?? "" }
    ];

    const hoofdcode_items = [
        { label: "Volgnummer:", value: hc?.volgnummer ?? "" },
        { label: "Hoofdtekst:", value: hc?.hoofdtekst ?? "" },
        { label: "Jaarversie:", value: hc?.versie_jaarversie ?? "" },
        { label: "Eenheid:", value: hc?.eenheid ?? "" },
        ...(is99Code ? [] : [
        ])
    ];

    let preceeded_by_items: { label: string, value: string }[] = [];
    if (hc?.preceeded_by) {
        preceeded_by_items = [
            { label: 'Hoofdcode:', value: hc.preceeded_by.label },
            { label: 'Jaarversie:', value: hc.preceeded_by.versie_jaarversie },
            { label: 'Romptekst:', value: hc.preceeded_by.romptekst ?? "" }
        ]
    }

    const bestekFilterCollapseIcon = (): JSX.Element => {
        if (usageChartFilterParamsExpanded) {
            return <IconContext.Provider value={{ size: '25px' }}><MdKeyboardArrowUp onClick={() => setUsageChartFilterParamsExpanded(false)} /></IconContext.Provider>
        } else {
            return <IconContext.Provider value={{ size: '25px' }}><MdKeyboardArrowDown onClick={() => setUsageChartFilterParamsExpanded(true)} /></IconContext.Provider>
        }
    }

    const exportButtonComponent = (): JSX.Element => {
        return (
            <>
                <div className="me-2" style={{ fontSize: '12px', marginTop: '4px' }}>Exporteer: </div>
                <ButtonGroup size={"sm"}>
                    <Button variant="light" onClick={() => download('csv')} disabled={isExporting}>
                        CSV
                    </Button>
                    {!is99Code && (
                        <Button variant="success" onClick={() => download('xlsx')} disabled={isExporting}>
                            Excel (met Deficodes)
                        </Button>
                    )}
                    {is99Code && (
                        <Button variant="success" onClick={() => download('xlsx')} disabled={isExporting}>
                            Excel
                        </Button>
                    )}
                </ButtonGroup>
            </>
        )
    }

    const specificDeficodeSwitchComponent = (): JSX.Element => {
        return (
            <Form.Check type={'switch'} checked={deficodeMode} onChange={(e) => handleDeficodeModeChange(e.target.checked)}></Form.Check>
        )
    }

    const deficodeSelectOptions: Deficode[] = hc?.deficodes ?? [];

    const extraBestekListColumns = (header: boolean, bestek?: Bestek): JSX.Element => {
        if (header) {
            return (
                <>
                    <th>Hoofdcode vermeldingen</th>
                    {!is99Code && <th>Hoofdcode hoeveelheid</th>}
                    {
                        deficodeMode && !is99Code && (
                            <>
                                <th>Deficode vermeldingen</th>
                                <th>Deficode hoeveelheid</th>
                            </>
                        )
                    }
                </>
            )
        } else {
            return (
                <>
                    <td>{ bestek?.hoofdcode_usage?.vermeldingen || (is99Code ? 'N/A' : 0) }</td>
                    {!is99Code && (
                        <td>{ `${Math.round((bestek?.hoofdcode_usage?.hoeveelheid ?? 0) * 100) / 100} ${hc?.eenheid ? hc?.eenheid : ''}` }</td>
                    )}
                    {
                        deficodeMode && !is99Code && (
                            <>
                                <td>{ bestek?.deficode_usage?.vermeldingen }</td>
                                <td>{ Math.round((bestek?.deficode_usage?.hoeveelheid ?? 0) * 100) / 100 } { selectedDeficode?.eenheid ? selectedDeficode?.eenheid : '' }</td>
                            </>
                        )
                    }
                </>
            )
        }
    }

    if (isInitialLoading) {
        return (
            <div style={{ 
                height: '100vh', 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center' 
            }}>
                <Spinner animation="border" variant="primary" />
                <span className="mt-3">Hoofdcode details ophalen...</span>
            </div>
        );
    }

    return (
        <>
            <div className={styles.header}>
                <IconContext.Provider value={{size: '40px'}}><MdKeyboardArrowLeft onClick={() => navigate(-1)} /></IconContext.Provider>
                <div className={styles.header_title}>
                    {hc?.label} - {
                        hc?.volgnummer === '99' 
                            ? "Vrije tekst" 
                            : (hc?.hoofdtekst || hc?.romptekst || "")
                    }
                </div>
            </div>

            <Container fluid className="pb-4">
                <Row className="mt-4" style={{ rowGap: '25px' }}>
                    {
                        hc && (
                            <Col sm={12} md={6}>
                                { hc.werk_categorie && <HeaderedList title="Werkcategorie" items={werkcat_items} /> }
                                { hc.subwerk_categorie && <HeaderedList title="Subwerkcategorie" items={subcat_items} /> }
                                { hc.preceeded_by && <HeaderedList title="Opvolger van" items={preceeded_by_items} showExpand onShowExpandClick={() => navigate(`/hoofdcodes/${hc.preceeded_by.id}`)} /> }
                            </Col>
                        )
                    }
                    {
                        hc && (
                            <Col sm={12} md={6}>
                                <HeaderedList title="Hoofdcode" items={hoofdcode_items} />
                            </Col>
                        )
                    }

                    {
                        hc && (
                            <Col lg={12}>
                                <HeaderedContainer title="Gebruik" customRightComponent={exportButtonComponent()}>
                                    <Container fluid>
                                        <Row className="mt-4">
                                            <Col>
                                                <HeaderedContainer title="Bestek filter" customRightComponent={bestekFilterCollapseIcon()}>
                                                    {
                                                        usageChartFilterParamsExpanded && (
                                                            <div className="mt-3">
                                                                <BestekFilter stateContext="local" initialLocalParams={initialUsageChartFilterParams} onFilterButtonClick={p => handleUsageChartFilterParamsChange(p)} disableHoofdCodeFiltering="full" />
                                                            </div>
                                                        )
                                                    }
                                                </HeaderedContainer>

                                                {!is99Code && (
                                                    <div className="mt-4">
                                                        <HeaderedContainer title="Specifieke deficode" customRightComponent={specificDeficodeSwitchComponent()}>
                                                            {
                                                                deficodeMode && (hc?.deficodes?.length ?? 0) > 0 && (
                                                                    <Container fluid>
                                                                        <Row className="mb-3">
                                                                            <Col xs={12} md={12}>
                                                                                <Select 
                                                                                    inputId="deficode-select-box" 
                                                                                    options={deficodeSelectOptions}
                                                                                    value={selectedDeficode ?? null}
                                                                                    getOptionLabel={(dc) => `${dc.positie}:${dc.inhoud_nr} - ${dc.inhoud_nr === 9 ? 'Vrije tekst' : (dc.inhoud_tekst ? dc.inhoud_tekst : (dc.bouwstof_omschrijving ? `${dc.bouwstof_omschrijving}${dc.eenheid ? ' (' + dc.eenheid + ')' : ''}` : ''))}`}
                                                                                    getOptionValue={(dc) => dc.id.toString()} 
                                                                                    onChange={(v) => handleSelectedDeficodeChange(v ? v : undefined)} />
                                                                            </Col>
                                                                        </Row>
                                                                    </Container>
                                                                )
                                                            }
                                                            {
                                                                deficodeMode && (hc?.deficodes?.length ?? 0) === 0 && <span>Er zijn geen deficodes om te selecteren.</span>
                                                            }
                                                        </HeaderedContainer>
                                                    </div>
                                                )}

                                                <HoofdcodeUsageChart
                                                    hoofdcodeID={hc.id}
                                                    eenheid={hc.eenheid}
                                                    bestekFilterParams={usageChartFilterParams}
                                                    deficode={deficodeMode ? selectedDeficode : undefined}
                                                    preloadedData={preloadedChartData}
                                                    totaalOverride={hc.hoofdcode_usage ? {
                                                        bestekken: hc.hoofdcode_usage.bestekken,
                                                        vermeldingen: hc.hoofdcode_usage.vermeldingen,
                                                        hoeveelheid: hc.hoofdcode_usage.hoeveelheid ?? 0
                                                    } : undefined}
                                                />

                                                <div className="mt-4">
                                                    <HeaderedContainer title="Bestekken">
                                                        <BestekkenList 
                                                            openInModal
                                                            loading={bestekListLoading}
                                                            bestekken={bestekListBestekken ?? []}
                                                            page={bestekListPage}
                                                            onPageChange={setBestekListPage}
                                                            extraColumns={extraBestekListColumns}
                                                            totalCount={bestekListCount}
                                                        />
                                                    </HeaderedContainer>
                                                </div>
                                            </Col>
                                        </Row>
                                    </Container>
                                   
                                </HeaderedContainer>
                            </Col>
                        )
                    }
                    
                </Row>
            </Container>
        </>
    )
}